import {
  FieldParameter,
  ResourceType,
  TemplateInfo,
} from "@fartherfinance/frontend/api/TaskWorkflows/Types";
import { getFieldKey } from "@fartherfinance/frontend/api/TaskWorkflows/utils/fieldHelpers";
import {
  isEnumData,
  isPrimitiveData,
} from "@fartherfinance/frontend/api/TaskWorkflows/utils/guards";

import { FormData } from "../core/types";

/**
 * Type guard to check if value has a 'value' property
 */
function hasValueProperty(value: unknown): value is { value: unknown } {
  return typeof value === "object" && value !== null && "value" in value;
}

/**
 * Type guard to check if value has a 'typeId' property
 */
function hasTypeIdProperty(value: unknown): value is { typeId: unknown } {
  return typeof value === "object" && value !== null && "typeId" in value;
}

/**
 * Extract task type ID from various value formats
 * Handles different ways task type might be stored in form fields
 */
export function extractTaskTypeId(taskTypeValue: unknown): string | null {
  if (!taskTypeValue) {
    return null;
  }

  if (typeof taskTypeValue === "string") {
    return taskTypeValue;
  }

  if (
    hasValueProperty(taskTypeValue) &&
    typeof taskTypeValue.value === "string"
  ) {
    return taskTypeValue.value;
  }

  if (hasTypeIdProperty(taskTypeValue)) {
    const typeId = taskTypeValue.typeId;
    return typeof typeId === "string" ? typeId : typeId?.toString() ?? null;
  }

  return null;
}

/**
 * Result of field rollover operation
 */
interface FieldRolloverResult {
  /** Successfully rolled over values */
  rolledOver: FormData;
  /** Fields that were discarded */
  discarded: string[];
}

/**
 * Main function to perform field rollover when switching between task templates.
 * This preserves compatible field values and discards incompatible ones.
 */
export function performTaskTemplateRollover(
  currentValues: FormData,
  oldTemplate: TemplateInfo,
  newTemplate: TemplateInfo,
  triggerField: string = "taskType"
): FieldRolloverResult {
  const result: FieldRolloverResult = {
    rolledOver: {},
    discarded: [],
  };

  // Create maps for easy field lookup
  const oldFields = new Map(
    oldTemplate.parameters.map((param) => [getFieldKey(param), param])
  );
  const newFields = new Map(
    newTemplate.parameters.map((param) => [getFieldKey(param), param])
  );

  // Process each current form value
  Object.entries(currentValues).forEach(([fieldKey, fieldValue]) => {
    // Skip the trigger field (e.g., taskType) - let it keep its current value
    if (fieldKey === triggerField) {
      return;
    }

    const oldField = oldFields.get(fieldKey);
    const newField = newFields.get(fieldKey);

    // If field doesn't exist in new template, discard it
    if (!newField) {
      result.discarded.push(fieldKey);
      return;
    }

    // Check if the field value is compatible with the new field
    if (isFieldValueCompatible(fieldValue, oldField, newField)) {
      result.rolledOver[fieldKey] = fieldValue;
    } else {
      result.discarded.push(fieldKey);
    }
  });

  return result;
}

/**
 * Deep equality check for objects, handling arrays and nested structures
 */
function deepEqual(obj1: unknown, obj2: unknown): boolean {
  if (obj1 === obj2) {
    return true;
  }

  if (obj1 == null || obj2 == null) {
    return obj1 === obj2;
  }

  if (typeof obj1 !== typeof obj2) {
    return false;
  }

  if (typeof obj1 !== "object") {
    return obj1 === obj2;
  }

  if (Array.isArray(obj1) !== Array.isArray(obj2)) {
    return false;
  }

  if (Array.isArray(obj1)) {
    if (!Array.isArray(obj2) || obj1.length !== obj2.length) {
      return false;
    }
    return obj1.every((item, index) => deepEqual(item, obj2[index]));
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  return keys1.every((key) =>
    deepEqual(
      (obj1 as Record<string, unknown>)[key],
      (obj2 as Record<string, unknown>)[key]
    )
  );
}

/**
 * Check if enum resource types are compatible, considering complex nested structures
 */
function areEnumResourceTypesCompatible(
  oldResourceType: ResourceType | undefined,
  newResourceType: ResourceType | undefined
): boolean {
  if (!oldResourceType || !newResourceType) {
    return true; // No resource type info, assume compatible
  }

  // Basic type must match
  if (oldResourceType.type !== newResourceType.type) {
    return false;
  }

  // For complex types, check all nested properties
  const resourceType = oldResourceType.type;

  switch (resourceType) {
    case "Person":
      // Person types must have same kinds array
      return deepEqual(oldResourceType.kinds, newResourceType.kinds);

    case "Assignee":
      // Assignee types must have same params structure
      return deepEqual(oldResourceType.params, newResourceType.params);

    case "TextEnum":
      // TextEnum must have same enum values
      return deepEqual(
        (
          oldResourceType.params as
            | Record<string, { value?: unknown }>
            | undefined
        )?.enums?.value,
        (
          newResourceType.params as
            | Record<string, { value?: unknown }>
            | undefined
        )?.enums?.value
      );

    case "StaticEnum":
      // StaticEnum must have same kinds and params
      return (
        deepEqual(oldResourceType.kinds, newResourceType.kinds) &&
        deepEqual(oldResourceType.params, newResourceType.params)
      );

    default:
      // For other types, deep compare the entire resource type structure
      return deepEqual(oldResourceType, newResourceType);
  }
}

/**
 * Check if a field value is compatible between old and new field definitions.
 * This ensures that data types and formats match before preserving values.
 */
function isFieldValueCompatible(
  value: unknown,
  oldField: FieldParameter | undefined,
  newField: FieldParameter
): boolean {
  // Null/undefined values are always compatible
  if (value == null) {
    return true;
  }

  // If no old field info, assume compatible (new field)
  if (!oldField) {
    return true;
  }

  // If data types are different, they're incompatible
  const oldType = oldField.dataType?.type;
  const newType = newField.dataType?.type;

  if (oldType !== newType) {
    return false;
  }

  // For enum fields, check if they use compatible resource types
  if (newField.dataType && isEnumData(newField.dataType)) {
    const oldEnumData =
      oldField.dataType && isEnumData(oldField.dataType)
        ? oldField.dataType
        : undefined;
    const newEnumData = newField.dataType;

    const oldResourceType = oldEnumData?.resourceType;
    const newResourceType = newEnumData.resourceType;

    return areEnumResourceTypesCompatible(oldResourceType, newResourceType);
  }

  // For primitive fields, check if formats match
  if (newField.dataType && isPrimitiveData(newField.dataType)) {
    const oldPrimitiveData =
      oldField.dataType && isPrimitiveData(oldField.dataType)
        ? oldField.dataType
        : undefined;
    const newPrimitiveData = newField.dataType;

    const oldFormat = oldPrimitiveData?.format;
    const newFormat = newPrimitiveData.format;

    // Same format or no format info is compatible
    if (!oldFormat || !newFormat || oldFormat === newFormat) {
      return true;
    }

    return false;
  }

  // Default to incompatible for other types
  return false;
}
